import { Box, Typography, Button, TextField, FormControl, useTheme } from "@mui/material";
import ApiClient from "../../apis/Api-client";
import { END_POINT } from "../../utils/End-points";
import { errorToster, successToster } from "../../shared/toster/Toster";
import { format } from "date-fns";
import { Helper } from "../../utils/Helpers";
import { Controller, useForm } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { OCCUPANCY_STATUSES } from "../../utils/Constants";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectTriggered, setTriggered } from "../../core/store/redux/slices/DashboardApi-slice";
import { Can, FEATURES, ACTIONS } from "../../core/permissions";
const {
  getLocalTimeFromUtc,
  isValidDate,
  getGradientTextStyle,
  toDateTimeLocalInputValue,
} = new Helper();

const ExtendStay = ({
  bookingData,
  handleCloseModal = () => {},
  handleClosePopover = () => {},
  resetData = () => {},
}) => {
  const { t } = useTranslation();
  const { api } = ApiClient();
  const { palette } = useTheme();
  const dispatch = useDispatch();
  const triggered = useSelector(selectTriggered);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const {
    name,
    room_no,
    room_id,
    reservation_id,
    occupancy_id,
    room_rent,
    stay_type_id,
    booking_status,
    checkout_date,
    checkin_grace_hour,
    total_tax_rate
  } = bookingData ?? {};

  const isoccupancyExtendStay = booking_status === OCCUPANCY_STATUSES.CHECK_IN;

  const translationKey = isoccupancyExtendStay
    ? "common_components.extend_stay_popup.toast.checkin_extended"
    : "common_components.extend_stay_popup.toast.reservation_extended";

  const formConfig = () =>
    Yup.object().shape({
      checkin_date: Yup.date()
        .required("Check in date must be a valid date")
        .typeError("Check in date must be a valid date")
        .test({
          name: "valid-date",
          message: "Check in date must be a valid date",
          test: (value) => isValidDate(value),
        }),
      expected_checkout_date: Yup.date()
        .required("Check out date must be a valid date")
        .typeError("Check out date must be a valid date")
        .test({
          name: "valid-date",
          message: "Check-out date must be a valid date",
          test: (value) => isValidDate(value),
        })
        .test({
          name: "checkout-after-checkin",
          message:
            "Check out date must be greater than previous Check out date",
          test: function (value) {
            return (
              !getLocalTimeFromUtc(checkout_date)?.localTime ||
              !value ||
              value > getLocalTimeFromUtc(checkout_date).localTime
            );
          },
        }),
    });

  const methods = useForm({
    resolver: yupResolver(formConfig()),
    defaultValues: {
      checkin_date:
        !!bookingData?.checkin_date &&
        getLocalTimeFromUtc(bookingData?.checkin_date)?.dateTimeString,
      expected_checkout_date:
        !!checkout_date && getLocalTimeFromUtc(checkout_date)?.dateTimeString,
    },
  });

  const {
    handleSubmit,
    formState: { errors },
    watch,
  } = methods;

  const { expected_checkout_date } = watch();
  const getMinCheckoutDate = () => {
    const now = new Date();
    const checkoutDate = checkout_date ? new Date(checkout_date) : null;
    return format(checkoutDate > now ? checkoutDate : now, "yyyy-MM-dd HH:mm");
  };

  const handleExtendStay = async (data) => {
    try {
      setIsSubmitted(true);
      let response;
      if (isoccupancyExtendStay) {
        response = await api.post(END_POINT.OCCUPANCY.EXTEND_OCCUPANCY, {
          checkin_date: format(
            new Date(data?.checkin_date),
            "yyyy-MM-dd HH:mm:ss"
          ),
          expected_checkout_date: format(
            new Date(data?.expected_checkout_date),
            "yyyy-MM-dd HH:mm:ss"
          ),
          past_checkout_date:checkout_date,
          room_id,
          occupancy_id,
          reservation_id,
          stay_type_id,
          // TODO: RATE_PLAN_CHANGE - Recalculate price based on Rate Plan for new dates
          occupancy_price: room_rent,
          checkin_grace_hour: 0,
          total_tax_rate
        });
      } else {
        response = await api.post(END_POINT.RESERVATION.EXTEND_RESERVATION, {
          expected_checkin_date: format(
            new Date(data?.checkin_date),
            "yyyy-MM-dd HH:mm:ss"
          ),
          expected_checkout_date: format(
            new Date(data?.expected_checkout_date),
            "yyyy-MM-dd HH:mm:ss"
          ),
          reserved_room_id: room_id,
          reservation_id,
          reservation_price: room_rent,
          stay_type_id,
          total_tax_rate
        });
      }
      if (response?.status === 200) {
        dispatch(setTriggered(!triggered));
        resetData();
        handleClosePopover();
        successToster(
          <Trans
            i18nKey={translationKey}
            values={{ name, roomNo: room_no }}
            components={{ strong: <strong /> }}
          />
        );
      }
    } catch (error) {
      errorToster(t("pages.booking_form.toast.another_reservation_found"));
      console.error("Error Extend Stay:", error);
    } finally {
      setTimeout(() => {
        setIsSubmitted(false);
      }, 2000);
    }
  };

  const fields = [
    {
      label: "Check in Date and Time",
      name: "checkin_date",
      disable: true,
      minDate: format(new Date(), "yyyy-MM-dd HH:mm"),
    },
    {
      label: "Check out Date and Time",
      name: "expected_checkout_date",
      disable: false,
      minDate: getMinCheckoutDate(),
    },
  ];

  return (
    <Box
      sx={{
        width: {
          xs: "74vw",
          md: "41vw",
        },
        "@media (min-width:900px) and (max-width:1180px)": {
          width: "54vw",
        },
        padding: { xs: "2% 0 3% 0", md: "0.5% 0 1.5% 0" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          margin: "0 auto 6% auto",
          width: "94%",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            fontSize: { xs: "3.59vw", sm: "2.398vw", md: "1.39vw" },
          }}
        >
          {t("common_components.extend_stay_popup.extend_stay")}
        </Typography>
        <Typography
          sx={{ fontSize: { md: "1.12vw" }, ...getGradientTextStyle(palette), fontWeight: 500 }}
        >
          {name}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "94%",
          margin: "0 auto",
        }}
      >
        <Box sx={{ width: "15%" }}>
          <Box
            sx={{
              backgroundColor: "#F7F2E8",
              borderRadius: "50%",
              width: { xs: "10vw", sm: "5vw", md: "2.92vw" },
              height: {
                xs: "10vw",
                sm: "5vw",
                md: "2.92vw",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: "3.08vw", sm: "1.44vw", md: "0.83vw" },
                color: "#252525",
                fontWeight: 600,
              }}
            >
              {room_no}
            </Typography>
          </Box>
        </Box>

        <form
          onSubmit={handleSubmit(handleExtendStay)}
          style={{ width: "85%" }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "space-between",
              width: 1,
            }}
          >
            {fields.map((formField, ind) => (
              <FormControl
                key={ind}
                sx={{ width: { xs: "100%", sm: "48%" }, marginBottom: "6%" }}
              >
                <Controller
                  {...methods}
                  name={formField.name}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => {
                    const minDateTime = toDateTimeLocalInputValue(formField.minDate);
                    const maxDateTime = toDateTimeLocalInputValue(formField.maxDate);
                    return (
                      <TextField
                        {...field}
                        name={formField.name}
                        size="small"
                        type="datetime-local"
                        label={t(
                          `common_components.extend_stay_popup.${formField?.label
                            .toLowerCase()
                            .replace(/ /g, "_")}`
                        )}
                        error={!!error?.message}
                        helperText={
                          error
                            ? t(
                                `common_components.extend_stay_popup.alert.${error?.message
                                  ?.toLowerCase()
                                  ?.replace(/ /g, "_")}`
                              )
                            : ""
                        }
                        value={watch(formField.name)}
                        disabled={formField?.disable}
                        onChange={(e) => {
                          let val = e.target.value;
                          const minVal = e.target.min;
                          const maxVal = e.target.max;
                          if (minVal && val < minVal) {
                            val = minVal;
                          } else if (maxVal && val > maxVal) {
                            val = maxVal;
                          }
                          e.target.value = val;
                          field.onChange(e);
                        }}
                        InputProps={{
                          sx: {
                            fontSize: { xs: "4.1vw", sm: "1.6vw", md: "1.12vw" },
                            "& input": {
                              lineHeight: 1.4,
                              py: { sm: 1.1, md: 1 },
                            },
                            "@media (min-width:900px) and (max-width:1180px)": {
                              fontSize: "0.98vw",
                            },
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          sx: {
                            fontSize: { xs: "4.1vw", sm: "1.45vw", md: "1.12vw" },
                            "@media (min-width:900px) and (max-width:1180px)": {
                              fontSize: "0.9vw",
                            },
                          },
                        }}
                        FormHelperTextProps={{
                          sx: {
                            fontSize: {
                              xs: "3.08vw",
                              sm: "1.44vw",
                              md: "0.83vw",
                            },
                          },
                        }}
                        inputProps={{
                          min: minDateTime,
                          max: maxDateTime,
                        }}
                      />
                    );
                  }}
                />
              </FormControl>
            ))}

            <Button
              onClick={handleCloseModal}
              variant="outlined"
              sx={{
                width: "48%",
                fontSize: { xs: "3.08vw", sm: "1.92vw", md: "1.12vw" },
                fontWeight: 700,
              }}
            >
              {t("common_components.extend_stay_popup.cancel")}
            </Button>
            <Can feature={FEATURES.RESERVATIONS} action={ACTIONS.EXTEND_STAY}>
              <Button
                variant="contained"
                disabled={
                  (checkout_date == expected_checkout_date ? true : false) ||
                  isSubmitted
                }
                type="submit"
                sx={{
                  width: "48%",
                  fontSize: { xs: "3.08vw", sm: "1.92vw", md: "1.12vw" },
                  fontWeight: 700,
                }}
              >
                {t("common_components.extend_stay_popup.confirm")}
              </Button>
            </Can>
          </Box>
        </form>
      </Box>
    </Box>
  );
};

export default ExtendStay;
